// Layer 3 (Transformation): pure Year-1 planning projections from the
// synthetic monthly profile, current readings and Module 5 forecast rules.

import type { SurfacePageTabItem } from '@/design-system';
import type { PlanningMonthProfile } from '@/versions/v1/schema/planning';
import { ROLE_TABLES } from '@/versions/v1/shared/bunker-hill-seed';
import {
  ANNUAL_PLAN_BUDGETS,
  ANNUAL_STRETCH_MULTIPLIER,
  BUDGET_TARGETS,
  DESIGN_AVAILABILITY_PCT,
  DESIGN_OPERATING_DAYS,
  DESIGN_THROUGHPUT_STPD,
  PLANNING_MONTH_PROFILE,
  PLANNING_RECOVERY_SCENARIOS,
} from '@/versions/v1/shared/bunker-hill-planning-seed';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { formatMoney, nsrRead } from '@/versions/v1/shared/NsrEngine';
import { JUNE_DAILY_PLAN_DST, completeProductionDays, juneMtdTonnes } from '@/versions/v1/shared/june-tonnage.derive';
import { evaluateCapacity } from '@/versions/v1/shared/CapacityEngine';
import type { CapacityCircuitId } from '@/versions/v1/schema/capacity';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { capacityPlantState } from '../capacity/Scene.transform';

export type PlanningTab = 'monthly' | 'annual' | 'forecast';

export const PLANNING_TABS: readonly SurfacePageTabItem<PlanningTab>[] = [
  { id: 'monthly', label: 'Monthly plan' },
  { id: 'annual', label: 'Annual budget vs stretch' },
  { id: 'forecast', label: 'Rolling forecast' },
];

export function planningTabFromSearch(search: string): PlanningTab {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const tab = new URLSearchParams(query).get('tab');
  return tab === 'annual' || tab === 'forecast' ? tab : 'monthly';
}

export type PlanningCopy = {
  title: string;
};

export function planningCopy(): PlanningCopy {
  return { title: 'Production planning' };
}

export type MonthlyPlanRow = {
  id: PlanningMonthProfile['id'];
  label: string;
  budgetStpd: number;
  scheduledDays: number;
  budgetSt: number;
  planSt: number;
  stretchSt: number;
  actualSt?: number;
  actualZnRecovery?: number;
  vsBudgetPct?: number;
  /** For the M4 row: the plan for the actual's own window (D25 complete
   *  production days × the daily plan), printed in the row so the `vs Budget`
   *  cell names what it compares against. */
  actualWindowPlanSt?: number;
  budgetZnRecovery: number;
  deltaZnPpt?: number;
  provenance: string;
};

export type PlanningScenarioName = 'Conservative' | 'Central' | 'Optimistic';

export type ScenarioTonnes = {
  name: PlanningScenarioName;
  rateStph: number;
  tonnes: number;
};

export type MonthlyPlanView = {
  rows: MonthlyPlanRow[];
  yearToDateActualSt: number;
  yearToDatePlanSt: number;
  yearToDateBudgetSt: number;
  yearToDateVsBudgetPct: number;
  yearToDateZnRecoveryPct: number;
  yearToDateZnBudgetPct: number;
  fullYearForecastSt: number;
  forecastToBudgetPct: number;
  forecastRemainingHours: number;
  /** The M4 MTD actual's own window name — `1–7 Jun` (D25 complete production days). */
  mtdWindowLabel: string;
  forecastScenarios: ScenarioTonnes[];
  ceilingCheck: {
    dailyTargetStpd: number;
    ceilingStph: number;
    ceilingStpd: number;
    /** D27: the binding limit's name, as `/capacity` prints it. */
    ceilingLabel: string;
    overrideVisible: boolean;
  };
};

export type AnnualPlanRow = {
  id: string;
  label: string;
  unit: 'stpd' | '%' | 'dst/year';
  design: number;
  annualBudget: number;
  stretch: number;
  yearToDateActual: number;
  provenance: string;
};

export type Month4RecoveryTarget = {
  parameter: 'Pb recovery' | 'Zn recovery' | 'Ag recovery';
  budgetPct: number;
  designPct: number;
};

export type PlanningHierarchyRow = {
  id: string;
  label: string;
  budgetSt: number;
  planSt: number;
  ceilingSt: number;
  ceilingBasis: string;
  /** D27: `design` for the Year-1 design basis, the Capacity binding limit
   *  (`grinding ceiling`) for Month 4 and shorter periods. */
  capLabel: string;
};

export type AnnualPlanView = {
  rows: AnnualPlanRow[];
  annualBudgetSt: number;
  annualStretchSt: number;
  designCapacitySt: number;
  scheduledDays: number;
  yearToDateActualSt: number;
  yearToDateBudgetSt: number;
  yearToDateVsBudgetPct: number;
  annualZnRecoveryBudgetPct: number;
  annualZnRecoveryDesignPct: number;
  month4RecoveryTargets: Month4RecoveryTarget[];
  hierarchy: PlanningHierarchyRow[];
};

export type FullYearScenario = {
  name: PlanningScenarioName;
  fullYearTonnes: number;
  restOfYearZnRecoveryPct: number;
  vsBudgetPct: number;
  fullYearNsr: number;
  nsrVsBudget: number;
};

export type RollingForecastView = {
  kpis: {
    centralTonnes: number;
    conservativeTonnes: number;
    centralNsr: number;
    budgetNsr: number;
  };
  forecastToClose: {
    /** The Month-4 close instant as the glass writes it (`01 Jul 00:00`). */
    closeAt: string;
    remainingHours: number;
    actualMtdSt: number;
    centralPctOfPlan: number;
    stphNeededToClose: number;
    scenarios: ScenarioTonnes[];
  };
  sevenDayStph: number;
  conservativeStph: number;
  optimisticStph: number;
  /** D27: the Optimistic rate's basis, as `/capacity` names it (`grinding ceiling`). */
  optimisticBasis: string;
  optimisticProvenance: string;
  scenarios: FullYearScenario[];
  budgetNsr: number;
};

const MONTH_4_INDEX = 3;
const M4_PLAN_TARGET_STPD = JUNE_DAILY_PLAN_DST;
/** The one Month-4 close instant; the 552 h remaining and the panel's date
 *  string both read it. */
export const CLOSE_OF_M4 = '2026-07-01T00:00:00Z';
/** The seven-day rate sample: the last seven complete production days. */
const RATE_SAMPLE_DAYS = 7;

/** The word for the Year-1 design basis (1,800 stpd × 350 days) in the hierarchy. */
export const DESIGN_CAP_LABEL = 'design';

export type PlanningCeiling = {
  /** The Capacity engine's maximum achievable feed at the cursor's measured plant state. */
  stph: number;
  /** The binding limit, named as Capacity names it — `grinding ceiling` at the cursor. */
  label: string;
  provenance: string;
  /** D27 (orchestrator ruling): the recoveries the Capacity engine's Maximum
   *  achievable scenario reads at this rate — the Optimistic forecast's recoveries. */
  recoveries: { pb: number; zn: number; ag: number };
  recoveryProvenance: string;
};

/** D27: the glass name of each Capacity limit when it binds, as `/capacity`
 *  names it — an explicit lookup, so "Pb/Ag" and "Zn" keep their case. */
export const CEILING_LABEL_BY_CIRCUIT: Record<CapacityCircuitId, string> = {
  crushing: 'crushing ceiling',
  grinding: 'grinding ceiling',
  'lead-flotation': 'Pb/Ag flotation ceiling',
  'zinc-flotation': 'Zn flotation ceiling',
  dewatering: 'dewatering ceiling',
};
export const RECOVERY_FLOOR_LABEL = 'recovery floor';

/** D27 (K16): Planning's ceiling is the Capacity engine's maximum achievable feed
 *  — the same quantity `/capacity` prints as "Max achievable at current grade ·
 *  Grinding ceiling 79.2 stph". Paul's M5 v2.1 §5 defines the Optimistic forecast
 *  as MTD actual + remaining hours × Module 1 maximum achievable, and caps the
 *  monthly plan and every shorter period at the Module 1 grade-adjusted ceiling;
 *  the Capacity engine is Module 1's maximum-achievable calculation. It is read
 *  through the same cursor projection as `/capacity` (`capacityPlantState`), so
 *  the two routes cannot disagree. The 75 stph `rd-c-feed.design` nameplate is
 *  M5 §3's design maximum: it stays the annual budget's Design basis only. */
export function planningCeiling(state: SessionState): PlanningCeiling {
  const plantState = capacityPlantState(state);
  const read = evaluateCapacity({ grade: plantState.grade, seed: CAPACITY_SEED, plantState });
  const binding = read.circuits.find((circuit) => Math.abs(circuit.ceilingStph - read.maxAchievableStph) < 1e-9);
  const label = binding ? CEILING_LABEL_BY_CIRCUIT[binding.id as CapacityCircuitId] : RECOVERY_FLOOR_LABEL;
  const maximum = read.scenarios.find((scenario) => scenario.id === 'maximum');
  if (!maximum) throw new Error('CapacityEngine read has no Maximum achievable scenario');
  return {
    stph: read.maxAchievableStph,
    label,
    provenance: `[derived] CapacityEngine maximum achievable at the measured grade · ${label}`,
    recoveries: { pb: maximum.recoveries.pbPct, zn: maximum.recoveries.znPct, ag: maximum.recoveries.agPct },
    recoveryProvenance: `[derived] CapacityEngine Maximum achievable recoveries at ${read.maxAchievableStph.toFixed(1)} stph, carried through the remaining Year-1 forecast`,
  };
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** `01 Jul 00:00` — a close instant as the glass writes it, in UTC. */
export function formatCloseTimestamp(iso: string = CLOSE_OF_M4): string {
  const at = new Date(iso);
  const day = String(at.getUTCDate()).padStart(2, '0');
  const month = MONTH_ABBR[at.getUTCMonth()] ?? '';
  const hours = String(at.getUTCHours()).padStart(2, '0');
  const minutes = String(at.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${hours}:${minutes}`;
}

function monthScheduledDays(month: PlanningMonthProfile): number {
  return month.calendarDays - month.plannedShutdownDays;
}

/** A measured reading at the session cursor. Reads through `valueAt` — the
 *  same cursor projection `/capacity` makes (`capacityPlantState`) — so a moved
 *  cursor moves Planning's measured recoveries, grades and grades-as-actuals
 *  with it. */
function numberFromReading(id: string, state: SessionState): number {
  const projected = valueAt(id, state);
  if (projected !== undefined) return projected;
  const row = readingByIdLookup(id);
  const fallback = row?.value ?? row?.design;
  if (fallback === undefined) throw new Error(`Missing value on reading ${id}`);
  return fallback;
}

/** A design field — cursor-free: design is the nameplate, not a measurement. */
function designFromReading(id: string): number {
  const row = readingByIdLookup(id);
  const design = row?.design ?? row?.value;
  if (design === undefined) throw new Error(`Missing design on reading ${id}`);
  return design;
}

function gmBudgetConcentrateGrade(metal: 'pb' | 'zn'): number {
  const rowId = metal === 'pb' ? 'gm-r6' : 'gm-r5';
  const table = ROLE_TABLES.find((candidate) => candidate.roleId === 'gm' && candidate.tabId === 'overview');
  const value = table?.rows.find((candidate) => candidate.id === rowId)?.cells[2];
  if (!value) throw new Error(`Missing GM budget target ${rowId}`);
  return Number.parseFloat(value);
}

function budgetRecoveryTarget(parameter: 'Pb recovery' | 'Zn recovery' | 'Ag recovery'): number {
  const seed = BUDGET_TARGETS.find((row) => row.periodId === 'pp-m4' && row.parameter === parameter);
  const value = seed?.value.match(/^([\d.]+)%/)?.[1];
  if (!value) throw new Error(`Missing Month-4 budget target for ${parameter}`);
  return Number.parseFloat(value);
}

function formatPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

/** The Month-4 forecast starts where the actual stops (D25, K16): at 00:00 of the
 *  first production day the June-to-date actual does not count. At the demo
 *  cursor the actual covers production days 1–7 Jun, so the forecast starts
 *  08 Jun 00:00 and the open day is forecast, never missing. */
function month4RemainingFrom(state: SessionState): string {
  const days = juneMtdTonnes(state).days;
  const last = days[days.length - 1];
  if (!last) return `${productionMonthStart(state)}T00:00:00Z`;
  const next = new Date(`${last.productionDate}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().replace('.000Z', 'Z');
}

function productionMonthStart(state: SessionState): string {
  return `${state.cursor.slice(0, 7)}-01`;
}

function scenarioRates(state: SessionState): {
  dailyTonnes: number[];
  centralStph: number;
  sigmaStph: number;
  conservativeStph: number;
  ceilingStph: number;
  remainingHours: number;
} {
  // One June series (K16): the rate sample is the measured `rs-ore-td` series'
  // last seven complete production days — the same readings the June-to-date
  // actual sums — never the typed `M4_DAILY_ACTUALS`.
  const dailyTonnes = completeProductionDays(state).slice(-RATE_SAMPLE_DAYS).map((day) => day.dst);
  const rates = dailyTonnes.map((tonnes) => tonnes / 24);
  const centralStph = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  const variance = rates.reduce((sum, rate) => sum + (rate - centralStph) ** 2, 0) / (rates.length - 1);
  const sigmaStph = Math.sqrt(variance);
  const remainingFromMs = Date.parse(month4RemainingFrom(state));
  const monthCloseMs = Date.parse(CLOSE_OF_M4);
  const remainingHours = (monthCloseMs - remainingFromMs) / 3_600_000;
  return {
    dailyTonnes,
    centralStph,
    sigmaStph,
    conservativeStph: centralStph - sigmaStph,
    ceilingStph: planningCeiling(state).stph,
    remainingHours,
  };
}

/** The M4 month-to-date actual is the measured plant state, not a typed
 *  per-day sum: the one June-to-date tonnage function's read (D25 — the complete
 *  production days 1–7 Jun at the replay cursor: readings stamped 31 May 18:00 –
 *  06 Jun 18:00). At the demo cursor that is 12,401 dst over 7 production days,
 *  not the old 13,760 st rate-×-days figure, and the open 08 Jun day shift is in
 *  no total. */
function month4ActualTonnes(state: SessionState): number {
  return juneMtdTonnes(state).tonnesDst;
}

/** The plan for the M4 actual's own window — `dayCount × 1,440` = 10,080 dst over
 *  production days 1–7 Jun. The M4 row prints it beside the actual and computes
 *  its variance against it, so the comparison base is visible (review S3). */
function month4MtdPlanSt(state: SessionState): number {
  return juneMtdTonnes(state).planDst;
}

/** The window name beside the M4 MTD actual, so the measured figure names its
 *  own window on the glass — `1–7 Jun` (complete production days, D25). */
function month4MtdWindowLabel(state: SessionState): string {
  return juneMtdTonnes(state).windowLabel;
}

/** D25 (K16): the number of complete production days in the M4 actual — 7 at the
 *  demo cursor. Every Month-4 to-date quantity (budget, plan, Zn budget weight,
 *  availability hours, elapsed days) reads this window, so the actual and its
 *  comparators always cover the same days. */
function month4MtdDays(state: SessionState): number {
  return juneMtdTonnes(state).dayCount;
}

function yearToDateActualTonnes(state: SessionState): number {
  const priorMonths = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + (month.actualSt ?? 0),
    0,
  );
  return priorMonths + month4ActualTonnes(state);
}

function yearToDateBudgetTonnes(state: SessionState): number {
  const priorMonths = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + month.budgetStpd * monthScheduledDays(month),
    0,
  );
  return priorMonths + PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!.budgetStpd * month4MtdDays(state);
}

function yearToDatePlanTonnes(state: SessionState): number {
  const ceilingStpd = planningCeiling(state).stph * 24;
  const priorMonths = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce((sum, month) => {
    const budgetSt = month.budgetStpd * monthScheduledDays(month);
    return sum + Math.min(budgetSt, ceilingStpd * monthScheduledDays(month));
  }, 0);
  const current = PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!;
  const currentBudget = current.budgetStpd * month4MtdDays(state);
  return priorMonths + Math.min(currentBudget, ceilingStpd * month4MtdDays(state));
}

function weightedRecoveryToDate(metal: 'zn' | 'pb' | 'ag', state: SessionState): number {
  const element = metal === 'zn' ? 'Zn' : metal === 'pb' ? 'Pb' : 'Ag';
  const actualKey = `actual${element}RecoveryPct` as
    | 'actualZnRecoveryPct'
    | 'actualPbRecoveryPct'
    | 'actualAgRecoveryPct';
  const readingId = metal === 'zn' ? 'rd-kpi-zn-rec' : metal === 'pb' ? 'rd-kpi-pb-rec' : 'rd-kpi-ag-rec';
  const historical = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + (month.actualSt ?? 0) * (month[actualKey] ?? 0),
    0,
  );
  return (historical + month4ActualTonnes(state) * numberFromReading(readingId, state)) / yearToDateActualTonnes(state);
}

function weightedBudgetRecoveryToDate(metal: 'zn' | 'pb' | 'ag', state: SessionState): number {
  const element = metal === 'zn' ? 'Zn' : metal === 'pb' ? 'Pb' : 'Ag';
  const budgetKey = `budget${element}RecoveryPct` as
    | 'budgetZnRecoveryPct'
    | 'budgetPbRecoveryPct'
    | 'budgetAgRecoveryPct';
  const historical = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + month.budgetStpd * monthScheduledDays(month) * month[budgetKey],
    0,
  );
  const current = PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!;
  const currentMtdBudget = current.budgetStpd * month4MtdDays(state);
  const budgetRecovery = (historical + currentMtdBudget * current[budgetKey]) / yearToDateBudgetTonnes(state);
  return budgetRecovery;
}

function scenarioTonnes(rateStph: number, remainingHours: number, state: SessionState): number {
  return yearToDateActualTonnes(state) + forecastRemainingHours(state, remainingHours) * rateStph;
}

function futureScheduledHours(): number {
  return PLANNING_MONTH_PROFILE.slice(MONTH_4_INDEX + 1).reduce(
    (sum, month) => sum + monthScheduledDays(month) * 24,
    0,
  );
}

function forecastRemainingHours(state: SessionState, m4RemainingHours = scenarioRates(state).remainingHours): number {
  return m4RemainingHours + futureScheduledHours();
}

function yearOneBudgetSt(): number {
  return PLANNING_MONTH_PROFILE.reduce(
    (sum, month) => sum + month.budgetStpd * monthScheduledDays(month),
    0,
  );
}

function yearOneScheduledDays(): number {
  return PLANNING_MONTH_PROFILE.reduce((sum, month) => sum + monthScheduledDays(month), 0);
}

function getScenarioTonnageRows(state: SessionState): ScenarioTonnes[] {
  const rates = scenarioRates(state);
  return [
    { name: 'Conservative', rateStph: rates.conservativeStph, tonnes: scenarioTonnes(rates.conservativeStph, rates.remainingHours, state) },
    { name: 'Central', rateStph: rates.centralStph, tonnes: scenarioTonnes(rates.centralStph, rates.remainingHours, state) },
    { name: 'Optimistic', rateStph: rates.ceilingStph, tonnes: scenarioTonnes(rates.ceilingStph, rates.remainingHours, state) },
  ];
}

export function monthlyPlanView(state: SessionState): MonthlyPlanView {
  const ceilingStph = planningCeiling(state).stph;
  const month4Actual = month4ActualTonnes(state);
  const month4ZnRecovery = numberFromReading('rd-kpi-zn-rec', state);
  const rows: MonthlyPlanRow[] = PLANNING_MONTH_PROFILE.map((month, index) => {
    const scheduledDays = monthScheduledDays(month);
    const budgetSt = month.budgetStpd * scheduledDays;
    const planSt = Math.min(budgetSt, ceilingStph * 24 * scheduledDays);
    const actualSt = index < MONTH_4_INDEX
      ? month.actualSt
      : index === MONTH_4_INDEX
        ? month4Actual
        : undefined;
    const actualZnRecovery = index < MONTH_4_INDEX
      ? month.actualZnRecoveryPct
      : index === MONTH_4_INDEX
        ? month4ZnRecovery
        : undefined;
    const budgetToDateSt = index === MONTH_4_INDEX
      ? month.budgetStpd * month4MtdDays(state)
      : budgetSt;
    // D25: the M4 actual is the complete production days 1–7, so its variance
    // reads against the plan for that same window (10,080 dst), printed in the
    // row — never the invisible 8-day pro-rata the review flagged (S3).
    const actualWindowPlanSt = index === MONTH_4_INDEX ? month4MtdPlanSt(state) : undefined;
    const varianceBaseSt = actualWindowPlanSt ?? budgetToDateSt;
    return {
      id: month.id,
      label: month.label,
      budgetStpd: month.budgetStpd,
      scheduledDays,
      budgetSt,
      planSt,
      stretchSt: budgetSt * ANNUAL_STRETCH_MULTIPLIER,
      actualSt,
      actualZnRecovery,
      vsBudgetPct: actualSt === undefined ? undefined : ((actualSt / varianceBaseSt) - 1) * 100,
      actualWindowPlanSt,
      budgetZnRecovery: month.budgetZnRecoveryPct,
      deltaZnPpt: actualZnRecovery === undefined ? undefined : actualZnRecovery - month.budgetZnRecoveryPct,
      provenance: index === MONTH_4_INDEX ? '[syn] M4 actuals reconciled to Reports' : month.provenance,
    };
  });
  const forecastScenarios = getScenarioTonnageRows(state);
  const central = forecastScenarios.find((scenario) => scenario.name === 'Central')!;
  const annualBudget = yearOneBudgetSt();
  return {
    rows,
    yearToDateActualSt: yearToDateActualTonnes(state),
    yearToDatePlanSt: yearToDatePlanTonnes(state),
    yearToDateBudgetSt: yearToDateBudgetTonnes(state),
    yearToDateVsBudgetPct: ((yearToDateActualTonnes(state) / yearToDateBudgetTonnes(state)) - 1) * 100,
    yearToDateZnRecoveryPct: weightedRecoveryToDate('zn', state),
    yearToDateZnBudgetPct: weightedBudgetRecoveryToDate('zn', state),
    fullYearForecastSt: central.tonnes,
    forecastToBudgetPct: (central.tonnes / annualBudget) * 100,
    forecastRemainingHours: forecastRemainingHours(state),
    mtdWindowLabel: month4MtdWindowLabel(state),
    forecastScenarios,
    ceilingCheck: {
      dailyTargetStpd: M4_PLAN_TARGET_STPD,
      ceilingStph,
      ceilingStpd: ceilingStph * 24,
      ceilingLabel: planningCeiling(state).label,
      overrideVisible: false,
    },
  };
}

function annualRecoveryBudget(metal: 'zn' | 'pb' | 'ag'): number {
  const key = metal === 'zn'
    ? 'budgetZnRecoveryPct'
    : metal === 'pb'
      ? 'budgetPbRecoveryPct'
      : 'budgetAgRecoveryPct';
  const scheduledDays = yearOneScheduledDays();
  return PLANNING_MONTH_PROFILE.reduce(
    (sum, month) => sum + monthScheduledDays(month) * month[key],
    0,
  ) / scheduledDays;
}

function annualRecoveryActual(metal: 'zn' | 'pb' | 'ag', state: SessionState): number {
  return weightedRecoveryToDate(metal, state);
}

function month4RecoveryTargets(): Month4RecoveryTarget[] {
  return [
    { parameter: 'Pb recovery', budgetPct: budgetRecoveryTarget('Pb recovery'), designPct: designFromReading('rd-kpi-pb-rec') },
    { parameter: 'Zn recovery', budgetPct: budgetRecoveryTarget('Zn recovery'), designPct: designFromReading('rd-kpi-zn-rec') },
    { parameter: 'Ag recovery', budgetPct: budgetRecoveryTarget('Ag recovery'), designPct: designFromReading('rd-kpi-ag-rec') },
  ];
}

function concentrateTonnes(
  feedSt: number,
  headGradePct: number,
  recoveryPct: number,
  concentrateGradePct: number,
): number {
  return feedSt * (headGradePct / 100) * (recoveryPct / 100) / (concentrateGradePct / 100);
}

function annualConcentrateBudget(metal: 'pb' | 'zn', state: SessionState): number {
  const headGrade = numberFromReading(metal === 'pb' ? 'rd-head-pb' : 'rd-head-zn', state);
  const budgetGrade = gmBudgetConcentrateGrade(metal);
  return PLANNING_MONTH_PROFILE.reduce((sum, month) => {
    const recovery = metal === 'pb' ? month.budgetPbRecoveryPct : month.budgetZnRecoveryPct;
    const tonnes = month.budgetStpd * monthScheduledDays(month);
    return sum + concentrateTonnes(tonnes, headGrade, recovery, budgetGrade);
  }, 0);
}

function annualConcentrateDesign(metal: 'pb' | 'zn'): number {
  const headGrade = designFromReading(metal === 'pb' ? 'rd-head-pb' : 'rd-head-zn');
  const recovery = designFromReading(metal === 'pb' ? 'rd-kpi-pb-rec' : 'rd-kpi-zn-rec');
  const concentrateGrade = designFromReading(metal === 'pb' ? 'rd-kpi-pb-grade' : 'rd-kpi-zn-grade');
  return concentrateTonnes(DESIGN_THROUGHPUT_STPD * DESIGN_OPERATING_DAYS, headGrade, recovery, concentrateGrade);
}

function yearToDateConcentrateActual(metal: 'pb' | 'zn', state: SessionState): number {
  const headGrade = numberFromReading(metal === 'pb' ? 'rd-head-pb' : 'rd-head-zn', state);
  const concentrateGrade = numberFromReading(metal === 'pb' ? 'rd-kpi-pb-grade' : 'rd-kpi-zn-grade', state);
  const recoveryId = metal === 'pb' ? 'rd-kpi-pb-rec' : 'rd-kpi-zn-rec';
  const recoveryKey = metal === 'pb' ? 'actualPbRecoveryPct' : 'actualZnRecoveryPct';
  const historic = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + concentrateTonnes(
      month.actualSt ?? 0,
      headGrade,
      month[recoveryKey] ?? 0,
      concentrateGrade,
    ),
    0,
  );
  return historic + concentrateTonnes(month4ActualTonnes(state), numberFromReading(metal === 'pb' ? 'rd-head-pb' : 'rd-head-zn', state), numberFromReading(recoveryId, state), concentrateGrade);
}

function pctBudgetStretch(budget: number, design?: number): number {
  const stretch = budget * ANNUAL_STRETCH_MULTIPLIER;
  return design === undefined ? stretch : Math.min(stretch, design);
}

function yearToDateAvailabilityActual(state: SessionState): number {
  const rows = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX + 1).map((month, index) => {
    const availabilityPct = month.actualMillAvailabilityPct;
    if (availabilityPct === undefined) throw new Error(`Missing [syn] availability actual for ${month.id}`);
    const days = index === MONTH_4_INDEX ? month4MtdDays(state) : monthScheduledDays(month);
    return { availabilityPct, hours: days * 24 };
  });
  const hours = rows.reduce((sum, row) => sum + row.hours, 0);
  return rows.reduce((sum, row) => sum + row.availabilityPct * row.hours, 0) / hours;
}

function month4PlanSt(state: SessionState): number {
  const month = PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!;
  const scheduledDays = monthScheduledDays(month);
  return Math.min(month.budgetStpd * scheduledDays, planningCeiling(state).stph * 24 * scheduledDays);
}

function hierarchyRows(annualBudgetSt: number, designCapacitySt: number, state: SessionState): PlanningHierarchyRow[] {
  // D27: Year 1 rests on the design maximum (M5 §3: 1,800 stpd × 350 days);
  // Month 4, the week and the day are capped at the Capacity engine's maximum
  // achievable at the measured grade (M5 §3–4), the same figure `/capacity` shows.
  const ceiling = planningCeiling(state);
  const capStph = ceiling.stph;
  const ceilingRate = `${capStph.toFixed(1)} stph`;
  const weekBudget = M4_PLAN_TARGET_STPD * 7;
  const dayBudget = M4_PLAN_TARGET_STPD;
  const annualCeiling = designCapacitySt;
  const month4Ceiling = capStph * 24 * 30;
  const weekCeiling = capStph * 24 * 7;
  const dayCeiling = capStph * 24;
  const monthBudget = PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!.budgetStpd * 30;
  return [
    { id: 'year', label: 'Year 1', budgetSt: annualBudgetSt, planSt: Math.min(annualBudgetSt, annualCeiling), ceilingSt: annualCeiling, ceilingBasis: `${DESIGN_THROUGHPUT_STPD.toLocaleString('en-US')} stpd × ${DESIGN_OPERATING_DAYS} days`, capLabel: DESIGN_CAP_LABEL },
    { id: 'month', label: 'Month 4 · Jun', budgetSt: monthBudget, planSt: month4PlanSt(state), ceilingSt: month4Ceiling, ceilingBasis: `${ceilingRate} × 24 h × 30 days`, capLabel: ceiling.label },
    { id: 'week', label: 'Week of 08 Jun', budgetSt: weekBudget, planSt: Math.min(weekBudget, weekCeiling), ceilingSt: weekCeiling, ceilingBasis: `${ceilingRate} × 24 h × 7 days`, capLabel: ceiling.label },
    { id: 'day', label: '08 Jun', budgetSt: dayBudget, planSt: Math.min(dayBudget, dayCeiling), ceilingSt: dayCeiling, ceilingBasis: `${ceilingRate} × 24 h`, capLabel: ceiling.label },
  ];
}

export function annualPlanView(state: SessionState): AnnualPlanView {
  const monthly = monthlyPlanView(state);
  const scheduledDays = yearOneScheduledDays();
  const annualBudgetSt = yearOneBudgetSt();
  const annualStretchSt = pctBudgetStretch(annualBudgetSt);
  const designCapacitySt = DESIGN_THROUGHPUT_STPD * DESIGN_OPERATING_DAYS;
  const znRecoveryBudget = annualRecoveryBudget('zn');
  const pbRecoveryBudget = annualRecoveryBudget('pb');
  const agRecoveryBudget = annualRecoveryBudget('ag');
  const pbBudgetGrade = gmBudgetConcentrateGrade('pb');
  const znBudgetGrade = gmBudgetConcentrateGrade('zn');
  const pbConcentrateBudget = annualConcentrateBudget('pb', state);
  const znConcentrateBudget = annualConcentrateBudget('zn', state);
  const elapsedScheduledDays = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce(
    (sum, month) => sum + monthScheduledDays(month),
    0,
  ) + month4MtdDays(state);
  const actualThroughputStpd = monthly.yearToDateActualSt / elapsedScheduledDays;
  const pbRecDesign = designFromReading('rd-kpi-pb-rec');
  const znRecDesign = designFromReading('rd-kpi-zn-rec');
  const agRecDesign = designFromReading('rd-kpi-ag-rec');
  const pbGradeDesign = designFromReading('rd-kpi-pb-grade');
  const znGradeDesign = designFromReading('rd-kpi-zn-grade');
  const pbGradeActual = numberFromReading('rd-kpi-pb-grade', state);
  const znGradeActual = numberFromReading('rd-kpi-zn-grade', state);
  const availabilityDesign = DESIGN_AVAILABILITY_PCT;
  const rows: AnnualPlanRow[] = [
    { id: 'throughput', label: 'Throughput (stpd)', unit: 'stpd', design: DESIGN_THROUGHPUT_STPD, annualBudget: M4_PLAN_TARGET_STPD, stretch: pctBudgetStretch(M4_PLAN_TARGET_STPD), yearToDateActual: actualThroughputStpd, provenance: '[syn] monthly budget profile · design throughput (1,800 stpd nameplate)' },
    { id: 'availability', label: 'Mill availability (%)', unit: '%', design: availabilityDesign, annualBudget: ANNUAL_PLAN_BUDGETS.millAvailabilityPct, stretch: pctBudgetStretch(ANNUAL_PLAN_BUDGETS.millAvailabilityPct, availabilityDesign), yearToDateActual: yearToDateAvailabilityActual(state), provenance: '[syn] hours-weighted M1–M3 monthly + 1–7 Jun (complete production days) availability profile' },
    { id: 'pb-recovery', label: 'Pb recovery (%)', unit: '%', design: pbRecDesign, annualBudget: pbRecoveryBudget, stretch: pctBudgetStretch(pbRecoveryBudget, pbRecDesign), yearToDateActual: annualRecoveryActual('pb', state), provenance: '[syn] ramp recovery profile · rd-kpi-pb-rec design' },
    { id: 'zn-recovery', label: 'Zn recovery (%)', unit: '%', design: znRecDesign, annualBudget: znRecoveryBudget, stretch: pctBudgetStretch(znRecoveryBudget, znRecDesign), yearToDateActual: annualRecoveryActual('zn', state), provenance: '[syn] ramp recovery profile · rd-kpi-zn-rec design' },
    { id: 'ag-recovery', label: 'Ag recovery (%)', unit: '%', design: agRecDesign, annualBudget: agRecoveryBudget, stretch: pctBudgetStretch(agRecoveryBudget, agRecDesign), yearToDateActual: annualRecoveryActual('ag', state), provenance: '[syn] ramp recovery profile · rd-kpi-ag-rec design' },
    { id: 'pb-grade', label: 'Pb con grade (% Pb)', unit: '%', design: pbGradeDesign, annualBudget: pbBudgetGrade, stretch: pctBudgetStretch(pbBudgetGrade, pbGradeDesign), yearToDateActual: pbGradeActual, provenance: 'GM performance table · rd-kpi-pb-grade' },
    { id: 'zn-grade', label: 'Zn con grade (% Zn)', unit: '%', design: znGradeDesign, annualBudget: znBudgetGrade, stretch: pctBudgetStretch(znBudgetGrade, znGradeDesign), yearToDateActual: znGradeActual, provenance: 'GM performance table · rd-kpi-zn-grade' },
    { id: 'pb-concentrate', label: 'Pb con produced (dst/year)', unit: 'dst/year', design: annualConcentrateDesign('pb'), annualBudget: pbConcentrateBudget, stretch: pctBudgetStretch(pbConcentrateBudget), yearToDateActual: yearToDateConcentrateActual('pb', state), provenance: '[derived] feed × head grade × recovery ÷ concentrate grade' },
    { id: 'zn-concentrate', label: 'Zn con produced (dst/year)', unit: 'dst/year', design: annualConcentrateDesign('zn'), annualBudget: znConcentrateBudget, stretch: pctBudgetStretch(znConcentrateBudget), yearToDateActual: yearToDateConcentrateActual('zn', state), provenance: '[derived] feed × head grade × recovery ÷ concentrate grade' },
  ];
  return {
    rows,
    annualBudgetSt,
    annualStretchSt,
    designCapacitySt,
    scheduledDays,
    yearToDateActualSt: monthly.yearToDateActualSt,
    yearToDateBudgetSt: monthly.yearToDateBudgetSt,
    yearToDateVsBudgetPct: monthly.yearToDateVsBudgetPct,
    annualZnRecoveryBudgetPct: znRecoveryBudget,
    annualZnRecoveryDesignPct: znRecDesign,
    month4RecoveryTargets: month4RecoveryTargets(),
    hierarchy: hierarchyRows(annualBudgetSt, designCapacitySt, state),
  };
}

function closeScenarios(state: SessionState): ScenarioTonnes[] {
  const rates = scenarioRates(state);
  const mtdActual = month4ActualTonnes(state);
  const close = (name: PlanningScenarioName, rateStph: number): ScenarioTonnes => ({
    name,
    rateStph,
    tonnes: mtdActual + rates.remainingHours * rateStph,
  });
  return [
    close('Conservative', rates.conservativeStph),
    close('Central', rates.centralStph),
    close('Optimistic', rates.ceilingStph),
  ];
}

function currentScenarioRecoveries(state: SessionState) {
  return {
    pb: numberFromReading('rd-kpi-pb-rec', state),
    zn: numberFromReading('rd-kpi-zn-rec', state),
    ag: numberFromReading('rd-kpi-ag-rec', state),
  };
}

function budgetRecoveriesForMonth(month: PlanningMonthProfile) {
  return {
    pb: month.budgetPbRecoveryPct,
    zn: month.budgetZnRecoveryPct,
    ag: month.budgetAgRecoveryPct,
  };
}

function actualRecoveriesForMonth(month: PlanningMonthProfile) {
  return {
    pb: month.actualPbRecoveryPct ?? 0,
    zn: month.actualZnRecoveryPct ?? 0,
    ag: month.actualAgRecoveryPct ?? 0,
  };
}

/** The recoveries each forecast scenario carries for the rest of Year 1, with
 *  their basis. D27 (orchestrator ruling, K16): Optimistic runs at the Capacity
 *  engine's maximum achievable rate, so it takes the recoveries the engine reads
 *  at that rate (its Maximum achievable scenario, Zn 80.6 % at the cursor), never
 *  the [syn] design set — one operating point, one recovery, on /planning and
 *  /capacity. Central and Conservative keep their [syn] seed assumptions. */
export function scenarioRecoveryBasis(name: PlanningScenarioName, state: SessionState): { recoveries: { pb: number; zn: number; ag: number }; provenance: string } {
  if (name === 'Optimistic') {
    const ceiling = planningCeiling(state);
    return { recoveries: ceiling.recoveries, provenance: ceiling.recoveryProvenance };
  }
  const scenario = PLANNING_RECOVERY_SCENARIOS.find((row) => row.name === name);
  if (!scenario) throw new Error(`Missing [syn] recovery assumptions for ${name}`);
  return { recoveries: scenario.recoveries, provenance: scenario.provenance };
}

function scenarioRecoveries(name: PlanningScenarioName, state: SessionState) {
  return scenarioRecoveryBasis(name, state).recoveries;
}

function annualBudgetNsr(state: SessionState): number {
  return PLANNING_MONTH_PROFILE.reduce((sum, month) => {
    const days = monthScheduledDays(month);
    const monthRead = nsrRead(state, month.budgetStpd, {
      budget: budgetRecoveriesForMonth(month),
    });
    return sum + monthRead.budgetDaily * days;
  }, 0);
}

function fullYearScenarioNsr(rateStph: number, forecastRecoveries: ReturnType<typeof scenarioRecoveries>, state: SessionState): number {
  const historical = PLANNING_MONTH_PROFILE.slice(0, MONTH_4_INDEX).reduce((sum, month) => {
    const days = monthScheduledDays(month);
    const actualTonnes = month.actualSt ?? 0;
    const monthRead = nsrRead(state, actualTonnes / days, {
      actual: actualRecoveriesForMonth(month),
    });
    return sum + monthRead.daily.total * days;
  }, 0);
  // One June series (K16): the M4 leg prices the measured complete production
  // days the June-to-date actual sums (12,401 dst over 1–7 Jun at the cursor),
  // not the typed `M4_DAILY_ACTUALS`; the open day is in the forecast leg.
  const m4Actual = juneMtdTonnes(state).days.reduce((sum, day) => {
    const dayRead = nsrRead(state, day.dst, { actual: currentScenarioRecoveries(state) });
    return sum + dayRead.daily.total;
  }, 0);
  const remainingForecastDays = forecastRemainingHours(state) / 24;
  const forecastRead = nsrRead(state, rateStph * 24, {
    actual: forecastRecoveries,
  });
  return historical + m4Actual + forecastRead.daily.total * remainingForecastDays;
}

export function rollingForecastView(state: SessionState): RollingForecastView {
  const rates = scenarioRates(state);
  const annual = annualPlanView(state);
  const m4Close = closeScenarios(state);
  const budgetNsr = annualBudgetNsr(state);
  const scenarioRows = getScenarioTonnageRows(state).map((scenario) => {
    const forecastRecoveryBasis = scenarioRecoveries(scenario.name, state);
    const nsr = fullYearScenarioNsr(scenario.rateStph, forecastRecoveryBasis, state);
    return {
      name: scenario.name,
      fullYearTonnes: scenario.tonnes,
      restOfYearZnRecoveryPct: forecastRecoveryBasis.zn,
      vsBudgetPct: ((scenario.tonnes / annual.annualBudgetSt) - 1) * 100,
      fullYearNsr: nsr,
      nsrVsBudget: nsr - budgetNsr,
    };
  });
  const scenarioOrder: readonly PlanningScenarioName[] = ['Optimistic', 'Central', 'Conservative'];
  const fullYearScenarios = scenarioOrder.map((name) => scenarioRows.find((row) => row.name === name)!);
  const central = fullYearScenarios.find((scenario) => scenario.name === 'Central')!;
  const conservative = fullYearScenarios.find((scenario) => scenario.name === 'Conservative')!;
  const centralClose = m4Close.find((scenario) => scenario.name === 'Central')!;
  const m4Budget = PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!.budgetStpd * monthScheduledDays(PLANNING_MONTH_PROFILE[MONTH_4_INDEX]!);
  return {
    kpis: {
      centralTonnes: central.fullYearTonnes,
      conservativeTonnes: conservative.fullYearTonnes,
      centralNsr: central.fullYearNsr,
      budgetNsr,
    },
    forecastToClose: {
      closeAt: formatCloseTimestamp(),
      remainingHours: rates.remainingHours,
      actualMtdSt: month4ActualTonnes(state),
      centralPctOfPlan: (centralClose.tonnes / m4Budget) * 100,
      stphNeededToClose: (m4Budget - month4ActualTonnes(state)) / rates.remainingHours,
      scenarios: m4Close,
    },
    sevenDayStph: rates.centralStph,
    conservativeStph: rates.conservativeStph,
    optimisticStph: rates.ceilingStph,
    optimisticBasis: planningCeiling(state).label,
    optimisticProvenance: planningCeiling(state).provenance,
    scenarios: fullYearScenarios,
    budgetNsr,
  };
}

export function formatPlanningValue(row: AnnualPlanRow, value: number): string {
  if (row.unit === 'stpd') return `${Math.round(value).toLocaleString('en-US')} stpd`;
  if (row.unit === 'dst/year') return `${Math.round(value).toLocaleString('en-US')} dst`;
  return formatPct(value);
}

export function formatPlanningMoney(value: number): string {
  return formatMoney(value);
}

export function formatSignedPercent(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
}

export function formatSignedPlanningMoney(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatMoney(Math.abs(value))}`;
}

export function planningProvenanceChip(full: string): { text: string; title: string } {
  const trimmed = full.trim();
  if (!trimmed) return { text: '', title: '' };
  if (trimmed.includes('[syn]')) return { text: '[syn]', title: trimmed };
  if (trimmed.includes('[illustrative]')) return { text: '[illustrative]', title: trimmed };
  if (trimmed.startsWith('[derived]') || trimmed.includes('NsrEngine')) return { text: 'derived', title: trimmed };
  return { text: 'source', title: trimmed };
}
